// src/pages/providers/2_dashboard/components/ArchivedPatientsTable.js
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { ChevronUpIcon, ChevronDownIcon, InformationCircleIcon } from '@heroicons/react/solid';
import { 
  UserIcon, 
  CalendarIcon, 
  DocumentDuplicateIcon,
  CheckCircleIcon,
  XCircleIcon,
  AnnotationIcon,
  ReplyIcon,
  TrashIcon
} from '@heroicons/react/outline';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { GlobalStateContext } from '../../../../context/GlobalStateContext';
import StatusMessage from '../../../../components/StatusMessage';
import { Tooltip } from 'react-tooltip';
import { doc, updateDoc, serverTimestamp, addDoc, collection } from 'firebase/firestore';
import { formatDateShort, compareTimestamps } from '../../../../utils/timestampUtils';
import { db, auth } from '../../../../firebase';
import PatientRestoreDialog from './PatientRestoreDialog';
import PatientRemoveForm from './PatientRemoveForm';
import useSessionState from '../../../../hooks/useSessionState';
import { DASHBOARD_KEYS } from '../../../../utils/dashboardViewState';
import CondensedPagination from '../../../../components/CondensedPagination';
import { emitPatientRestored } from '../../../../analytics/patientArchiveEvents';
import { emitCaseloadPatientOpened } from '../../../../analytics/caseloadNavEvents';
import DashboardPersonName from './DashboardPersonName';
import {
  getPatientOverviewPath,
  getPatientOverviewNavState,
} from '../utils/patientOverviewRoute';
import { orgShowsDashboardContractColumn } from '../../../../constants/assessmentTypesByOrg';
import PatientContractTypeBadge from '../../../../components/PatientContractTypeBadge';
import { matchesAssignedClinician } from '../../../../utils/assignedClinicianFilter';

function ArchivedPatientsTable({ activeFilters, onRecordPatientView }) {
  const { patientsData, patientsLoading, optimisticRestorePatient, refreshPatientsData, userData } = useContext(GlobalStateContext);
  const navigate = useNavigate();
  // Sort order is session-persisted (its own key, separate from the current
  // table) so the Archived tab also restores its sorting after navigating to a
  // patient and back. Pagination is intentionally not persisted.
  const [sortConfig, setSortConfig] = useSessionState(
    DASHBOARD_KEYS.archivedSort,
    () => ({ key: 'archivedAt', direction: 'descending' })
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [clickedButton, setClickedButton] = useState(null);
  const [restoringPatient, setRestoringPatient] = useState(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [patientToRestore, setPatientToRestore] = useState(null);
  const [removingPatient, setRemovingPatient] = useState(null);
  const patientsPerPage = 10;

  // Same TOC-only Contract column as the current-patients table, so archived
  // records stay comparable when a clinician switches tabs.
  const showContractColumn = orgShowsDashboardContractColumn(userData?.organisation);

  // Reset pagination when filters change to avoid landing on an empty page.
  // This was previously missing (unlike PatientTable), causing users on page N
  // to see blank results after tightening a filter that yields fewer pages.
  useEffect(() => {
    setCurrentPage(1);
  }, [activeFilters]);

  // Filter for archived patients only
  const archivedPatients = useMemo(() => {
    return patientsData.filter(patient => patient.isArchived === true);
  }, [patientsData]);

  // Apply filters to archived patients data
  const filteredPatients = useMemo(() => {
    return archivedPatients.filter(patient => {
      // Patient name filter
      const nameMatch = !activeFilters.patientName ||
        `${patient.firstName} ${patient.lastName}`
          .toLowerCase()
          .includes(activeFilters.patientName.toLowerCase());

      // Email filter
      const emailMatch = !activeFilters.emailAddress ||
        (patient.email || '').toLowerCase()
          .includes(activeFilters.emailAddress.toLowerCase());
      
      // Reference number filter
      const referenceMatch = !activeFilters.referenceNumber ||
      (patient.customFields?.referenceNumber || '').toLowerCase()
        .includes(activeFilters.referenceNumber.toLowerCase());

      // Payment method filter
      const paymentMatch = !activeFilters.paymentMethod ||
        patient.customFields?.paymentMethod === activeFilters.paymentMethod.value;

      // Contract Type filter (Well Life Clinic / The Owl Centre — labelled "Contract Type"
      // in the UI but stored as assessmentType in the backend)
      const assessmentTypeMatch = !activeFilters.assessmentType ||
        patient.customFields?.assessmentType === activeFilters.assessmentType.value;

      // Assessment module filter (Adult ADHD vs Adult Autism).
      // Missing assessmentModule defaults to 'adultAdhd' for backwards compatibility.
      const moduleMatch = !activeFilters.assessmentModule ||
        (patient.assessmentModule || 'adultAdhd') === activeFilters.assessmentModule.value;

      const assignedToMatch = matchesAssignedClinician(
        patient,
        activeFilters.assignedTo?.value
      );

      // Archive Reason filter (NEW - archive-specific)
      const archiveReasonMatch = !activeFilters.archiveReason ||
        patient.archiveReason === activeFilters.archiveReason.value;

      // Date Added range filter (filters by createdAt)
      let dateAddedMatch = true;
      if (activeFilters.dateRange?.startDate && activeFilters.dateRange?.endDate) {
        const createdDate = patient.createdAt?.toDate?.() || new Date(patient.createdAt);
        const startDate = new Date(activeFilters.dateRange.startDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(activeFilters.dateRange.endDate);
        endDate.setHours(23, 59, 59, 999);
        dateAddedMatch = createdDate >= startDate && createdDate <= endDate;
      }

      // Date Archived range filter (NEW - archive-specific, filters by archivedAt)
      let dateArchivedMatch = true;
      if (activeFilters.archiveDateRange?.startDate && activeFilters.archiveDateRange?.endDate) {
        const archiveDate = patient.archivedAt?.toDate?.() || new Date(patient.archivedAt);
        const startDate = new Date(activeFilters.archiveDateRange.startDate);
        startDate.setHours(0, 0, 0, 0);
        const endDate = new Date(activeFilters.archiveDateRange.endDate);
        endDate.setHours(23, 59, 59, 999);
        dateArchivedMatch = archiveDate >= startDate && archiveDate <= endDate;
      }

      // All conditions must be true
      return nameMatch && emailMatch && referenceMatch && paymentMatch && 
             assessmentTypeMatch && moduleMatch && assignedToMatch && archiveReasonMatch && 
             dateAddedMatch && dateArchivedMatch;
    });
  }, [archivedPatients, activeFilters]);

  const sortedPatients = useMemo(() => {
    let sortablePatients = [...filteredPatients];
    if (sortConfig.key) {
      sortablePatients.sort((a, b) => {
        // Date Archived / Date Added print only the calendar day. The stored
        // values still have a clock time, and that is what the sort uses, so
        // two patients archived (or added) on the same day come out in the
        // order they actually happened.
        if (sortConfig.key === 'archivedAt') {
          return compareTimestamps(a.archivedAt, b.archivedAt, sortConfig.direction);
        }

        if (sortConfig.key === 'createdAt') {
          return compareTimestamps(a.createdAt, b.createdAt, sortConfig.direction);
        }

        // Handle reference number in customFields
        if (sortConfig.key === 'referenceNumber') {
          const aValue = a.customFields?.referenceNumber || '';
          const bValue = b.customFields?.referenceNumber || '';
          return sortConfig.direction === 'ascending'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }

        // Handle archive reason
        if (sortConfig.key === 'archiveReason') {
          const aValue = a.archiveReason || '';
          const bValue = b.archiveReason || '';
          return sortConfig.direction === 'ascending'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        // Handle other fields
        const aValue = a[sortConfig.key] || '';
        const bValue = b[sortConfig.key] || '';
        return sortConfig.direction === 'ascending'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      });
    }
    return sortablePatients;
  }, [filteredPatients, sortConfig]);

  // Clamp currentPage when the filtered/sorted list shrinks (e.g. after a
  // restore removes a patient from the archived list). Without this, the user
  // could land on an empty page if the last patient on that page was restored.
  useEffect(() => {
    const maxPage = Math.ceil(sortedPatients.length / patientsPerPage);
    if (maxPage > 0 && currentPage > maxPage) {
      setCurrentPage(maxPage);
    }
  }, [sortedPatients.length, currentPage]);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey) => {
    // Always reserve space to prevent header width jank
    if (sortConfig.key !== columnKey) {
      return <span className="inline-block w-4 h-4 ml-1" aria-hidden="true"></span>;
    }
    return sortConfig.direction === 'ascending'
      ? <ChevronUpIcon className="w-4 h-4 ml-1 text-white" />
      : <ChevronDownIcon className="w-4 h-4 ml-1 text-white" />;
  };

  const copyPatientIdToClipboard = async (patientId) => {
    try {
      await navigator.clipboard.writeText(patientId);
      
      // Show blue button state temporarily
      setClickedButton(patientId);
      setTimeout(() => setClickedButton(null), 300); // Blue for 300ms
      
      // Show toast notification
      toast.success(`Patient ID ${patientId} has been copied to clipboard`);
      
    } catch (err) {
      console.error('Failed to copy patient ID:', err);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = patientId;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      // Show blue button state temporarily
      setClickedButton(patientId);
      setTimeout(() => setClickedButton(null), 300);
      
      // Show toast notification
      toast.success(`Patient ID ${patientId} has been copied to clipboard`);
    }
  };

  const openRestoreDialog = (patient) => {
    setPatientToRestore(patient);
    setRestoreDialogOpen(true);
  };

  const closeRestoreDialog = () => {
    if (!restoringPatient) {
      setRestoreDialogOpen(false);
      setPatientToRestore(null);
    }
  };

  const handleRestorePatient = async () => {
    if (!patientToRestore) return;

    setRestoringPatient(patientToRestore.patientId);
    
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User not authenticated');
      }

      const patientDocRef = doc(db, 'patients', patientToRestore.patientId);
      
      await updateDoc(patientDocRef, {
        isArchived: false,
        archiveReason: null,
        archiveNote: null,
        archivedAt: null,
        archivedBy: null,
        archivedByName: null,
        restoredAt: serverTimestamp(),
        restoredBy: user.uid,
        restoredByName: user.displayName || 'Unknown User'
      });

      // Analytics mirror of the state change that just persisted. Emitted after
      // the patient document update - which IS the state change - and before the
      // audit entry below, because the two writes are not atomic: if the audit
      // append fails on a flaky connection, the patient is still restored, and
      // an event stream that missed it would believe the patient archived until
      // reconciliation. No reason or note: the restore path records neither.
      emitPatientRestored({ patientId: patientToRestore.patientId });

      // Append event to subcollection for append-only audit trail
      await addDoc(collection(db, 'patients', patientToRestore.patientId, 'archive_events'), {
        action: 'restore',
        reason: null,
        note: null,
        byUserId: user.uid,
        byUserName: user.displayName || 'Unknown User',
        at: serverTimestamp()
      });

      // Optimistic (post-success) UI update:
      // Move the row immediately without waiting for the refetch.
      try {
        optimisticRestorePatient(patientToRestore.patientId);
      } catch (_) {}

      // Flash button state and reuse toast success styling
      setClickedButton(patientToRestore.patientId);
      setTimeout(() => setClickedButton(null), 300);

      toast.success(`Patient ${patientToRestore.firstName} ${patientToRestore.lastName} has been restored`);
      
      // Close dialog
      setRestoreDialogOpen(false);
      setPatientToRestore(null);

      // No force-refresh here — the optimistic state IS the truth (the write succeeded).
      // The next natural SWR cycle (30s TTL) will confirm with the server.

    } catch (error) {
      console.error('Error restoring patient:', error);
      toast.error('Failed to restore patient. Please try again.');
      // Ensure list is consistent even if we failed mid-flight
      try { await refreshPatientsData({ force: true }); } catch (_) {}
    } finally {
      setRestoringPatient(null);
    }
  };

  // Helper function to get unique clinicians with their stages
  const getUniqueClinicians = (patient) => {
    const cliniciansList = [];
    const clinicianMap = {};
    
    if (!patient.clinicians) return cliniciansList;
    
    const isAutismPatient = patient.assessmentModule === 'adultAutism';

    const stages = {
      'clinicianCI': { code: 'CI', name: isAutismPatient ? 'History' : 'Clinical Interview' },
      'clinicianSSI': { code: 'SSI', name: 'Semi-Structured Interview' },
      'clinicianDD': { code: 'DD', name: 'Diagnostic Decision' },
      'clinicianPAD': { code: 'PAD', name: isAutismPatient ? 'Feedback Session' : 'Post-Assessment Discussion' },
    };
    // OBS stage only exists for autism patients
    if (isAutismPatient) {
      stages['clinicianOBS'] = { code: 'OBS', name: 'Behavioural Observation' };
    }
    
    // Add primary clinicians
    Object.entries(stages).forEach(([stageKey, stageInfo]) => {
      const clinician = patient.clinicians[stageKey];
      if (clinician && clinician.clinicianId) {
        if (!clinicianMap[clinician.clinicianId]) {
          clinicianMap[clinician.clinicianId] = {
            clinicianId: clinician.clinicianId,
            clinicianName: clinician.clinicianName,
            stages: [stageInfo]
          };
        } else {
          clinicianMap[clinician.clinicianId].stages.push(stageInfo);
        }
      }
    });
    
    // Add supporting clinicians for Diagnostic Decision stage
    const ddClinician = patient.clinicians?.clinicianDD;
    if (ddClinician && ddClinician.supportingClinicianIds && 
        ddClinician.supportingClinicianIds.length > 0 &&
        ddClinician.supportingClinicianNames &&
        ddClinician.supportingClinicianNames.length > 0) {
      
      const ddStageInfo = { code: 'DD', name: 'Diagnostic Decision (MDT Review)' };
      
      for (let i = 0; i < ddClinician.supportingClinicianIds.length; i++) {
        const clinicianId = ddClinician.supportingClinicianIds[i];
        const clinicianName = ddClinician.supportingClinicianNames[i] || 'Unknown Clinician';
        
        if (!clinicianMap[clinicianId]) {
          clinicianMap[clinicianId] = {
            clinicianId: clinicianId,
            clinicianName: clinicianName,
            stages: [ddStageInfo]
          };
        } else {
          // If already in our map (e.g., they're primary for another stage),
          // just add DD supporting role
          clinicianMap[clinicianId].stages.push(ddStageInfo);
        }
      }
    }
    
    // Convert map to array
    Object.values(clinicianMap).forEach(clinician => {
      cliniciansList.push(clinician);
    });
    
    return cliniciansList;
  };

  // Get archive reason display info
  const getArchiveReasonInfo = (reason) => {
    switch (reason) {
      case 'completed':
        return {
          text: 'Completed',
          icon: CheckCircleIcon,
          color: 'text-green-600',
          bgColor: 'bg-green-100'
        };
      case 'not_proceeding':
        return {
          text: 'Not Proceeding',
          icon: XCircleIcon,
          color: 'text-gray-600',
          bgColor: 'bg-gray-200'
        };
      default:
        return {
          text: 'Unknown',
          icon: InformationCircleIcon,
          color: 'text-gray-600',
          bgColor: 'bg-gray-100'
        };
    }
  };

  if (sortedPatients.length === 0) {
    if (patientsLoading) {
      return (
        <div className="w-full h-24 flex items-center justify-center">
          <div className="inline-flex items-center px-4 py-2 bg-violet-100 text-slate-800 rounded-lg">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-slate-800" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Loading patients...</span>
          </div>
        </div>
      );
    }
    return (
      <StatusMessage
        color="gray"
        icon={<InformationCircleIcon className="w-5 h-5 mr-2" />}
      >
        No archived patient records found. Patients will appear here when they are marked as completed or not proceeding with assessment.
      </StatusMessage>
    );
  }

  // Pagination logic
  const indexOfLastPatient = currentPage * patientsPerPage;
  const indexOfFirstPatient = indexOfLastPatient - patientsPerPage;
  const currentPatients = sortedPatients.slice(indexOfFirstPatient, indexOfLastPatient);
  
  const totalPages = Math.ceil(sortedPatients.length / patientsPerPage);

  return (
    <div className="w-full space-y-4">
      <PatientRestoreDialog
        isOpen={restoreDialogOpen}
        onClose={closeRestoreDialog}
        patient={patientToRestore}
        onConfirm={handleRestorePatient}
        isRestoring={!!restoringPatient}
      />
      <div className="relative flex flex-col w-full h-full text-gray-700 bg-white border border-slate-200 shadow-sm rounded-lg isolate">
      <div className="overflow-auto">
        <table className="w-full text-left table-auto min-w-max">
          <thead>
            <tr>             
              <th 
                className={`p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200 ${showContractColumn ? 'w-[220px]' : 'w-[250px]'} sticky left-0 z-20 after:absolute after:top-0 after:left-full after:h-full after:w-4 after:bg-gradient-to-r after:from-black/[0.10] after:to-transparent after:pointer-events-none after:content-['']`}
                onClick={() => requestSort('firstName')}
              >
                <div className="flex items-center">
                  <p className="text-xs font-medium text-white">
                    Patient Name
                  </p>
                  {getSortIcon('firstName')}
                </div>
              </th>
              <th 
                className="p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200 w-[90px]"
                onClick={() => requestSort('referenceNumber')}
              >
                <div className="flex items-center">
                  <p className="text-xs font-medium text-white">
                    Reference
                  </p>
                  {getSortIcon('referenceNumber')}
                </div>
              </th>
              <th 
                className={`p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200 ${showContractColumn ? 'w-[140px]' : 'w-[160px]'}`}
                onClick={() => requestSort('createdAt')}
              >
                <div className="flex items-center">
                  <p className="text-xs font-medium text-white">
                    Date Added
                  </p>
                  {getSortIcon('createdAt')}
                </div>
              </th>
              <th className={`p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200 ${showContractColumn ? 'w-[130px]' : 'w-[155px]'}`}>
                <p className="text-xs font-medium text-white">
                  Pathway
                </p>
              </th>
              {showContractColumn && (
                <th className="p-3 border-b border-[#43213e] bg-[#43213e] w-[180px]">
                  <p className="text-xs font-medium text-white">
                    Contract
                  </p>
                </th>
              )}
              <th className={`p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200 ${showContractColumn ? 'w-[260px]' : 'w-[340px]'}`}>
                  <p className="text-xs font-medium text-white">
                  Assigned Clinicians
                  </p>
              </th>
              <th 
                className="p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200"
                onClick={() => requestSort('archivedAt')}
              >
                <div className="flex items-center">
                  <p className="text-xs font-medium text-white">
                    Date Archived
                  </p>
                  {getSortIcon('archivedAt')}
                </div>
              </th>
              <th 
                className="p-3 border-b border-[#43213e] bg-[#43213e] cursor-pointer transition-colors duration-200"
                onClick={() => requestSort('archiveReason')}
              >
                <div className="flex items-center">
                  <p className="text-xs font-medium text-white">
                    Archive Reason
                  </p>
                  {getSortIcon('archiveReason')}
                </div>
              </th>
              <th className="p-3 border-b border-[#43213e] bg-[#43213e]">
                <p className="text-xs font-medium text-white">
                  Actions
                </p>
              </th>
            </tr>
          </thead>
          <tbody>
            {currentPatients.map((patient) => {
              const reasonInfo = getArchiveReasonInfo(patient.archiveReason);
              const IconComponent = reasonInfo.icon;
              // Path + nav state are shared by the name link and the View
              // Patient button so the two entry points cannot drift. Adult
              // ADHD now also gets patientSummary (same as Current Patients)
              // so the loading screen can greet the incoming name.
              const overviewPath = getPatientOverviewPath(patient);
              const overviewState = getPatientOverviewNavState(patient);
              const displayName = `${patient.firstName || ''} ${patient.lastName || ''}`.trim();
              const isModular = patient.assessmentModule === 'adultAutism'
                || patient.assessmentModule === 'cypAdhd';

              return (
                <tr key={patient.patientId} className="group hover:bg-slate-50 border-b border-slate-200">
                  <td className={`px-3 py-2 ${showContractColumn ? 'w-[220px]' : 'w-[250px]'} sticky left-0 z-10 bg-white group-hover:bg-slate-50 after:absolute after:top-0 after:left-full after:h-full after:w-4 after:bg-gradient-to-r after:from-black/[0.07] after:to-transparent after:pointer-events-none after:content-['']`}>
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                        <UserIcon className="h-4 w-4 text-gray-600" />
                      </div>
                      <div className="min-w-0 flex-1">
                        {overviewPath ? (
                          <DashboardPersonName
                            to={overviewPath}
                            state={overviewState}
                            ariaLabel={`View patient overview for ${displayName || 'this patient'}`}
                            onClick={() => {
                              emitCaseloadPatientOpened({
                                patientId: patient.patientId,
                                trigger: 'name',
                                caseload: 'archived',
                              });
                              // Record for the recently-viewed shortcut on the dashboard
                              onRecordPatientView?.(patient);
                            }}
                          >
                            {patient.firstName} {patient.lastName}
                          </DashboardPersonName>
                        ) : (
                          <p className="block font-semibold text-xs text-slate-800 truncate">
                            {patient.firstName} {patient.lastName}
                          </p>
                        )}
                        <p className="text-xs text-slate-500 truncate">
                          {patient.email}
                        </p>
                      </div>
                    </div>
                  </td>

                  <td className="px-3 py-2 text-xs text-slate-600 w-[90px]">
                    <div className="truncate">
                    {patient.customFields?.referenceNumber || '-'}
                    </div>
                  </td>

                  <td className={`px-3 py-2 text-xs ${showContractColumn ? 'w-[140px]' : 'w-[160px]'}`}>
                    <div className="flex items-center space-x-2"
                     data-tooltip-id={`creator-${patient.patientId}`}>
                    <CalendarIcon className="h-4 w-4 text-slate-400 flex-shrink-0" />
                    <p className="text-xs text-slate-600 whitespace-nowrap">
                      {formatDateShort(patient.createdAt)}
                    </p>
                  </div>
                    <Tooltip
                      id={`creator-${patient.patientId}`}
                      place="top"
                      style={{
                        maxWidth: '250px',
                        whiteSpace: 'normal',
                        backgroundColor: 'rgba(34, 34, 34, 1)',
                        borderRadius: '10px',
                        color: '#fff',
                        fontWeight: "bold"
                      }}
                    >
                      Added by {patient.createdByName || 'Unknown'}
                    </Tooltip>
                  </td>

                  <td className={`px-3 py-2 ${showContractColumn ? 'w-[130px]' : 'w-[155px]'}`}>
                    {(() => {
                      const module = patient.assessmentModule || 'adultAdhd';
                      // Mirrors the pathway chip map in PatientTable.js so
                      // archived rows label every module correctly (a CYP row
                      // must never read "Adult ADHD").
                      const moduleChips = {
                        adultAdhd: { bg: 'bg-blue-100 text-blue-800', label: 'Adult ADHD' },
                        adultAutism: { bg: 'bg-teal-100 text-teal-800', label: 'Adult Autism' },
                        cypAdhd: { bg: 'bg-purple-100 text-purple-800', label: 'CYP ADHD' },
                      };
                      const chip = moduleChips[module] || moduleChips.adultAdhd;
                      return (
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${chip.bg}`}>
                          {chip.label}
                        </span>
                      );
                    })()}
                  </td>

                  {showContractColumn && (
                    <td className="px-3 py-2 w-[180px]">
                      <PatientContractTypeBadge
                        stack
                        organisation={userData?.organisation}
                        assessmentType={patient.customFields?.assessmentType}
                        subContractType={patient.customFields?.subContractType}
                      />
                    </td>
                  )}

                  <td className={`px-3 py-2 text-xs ${showContractColumn ? 'w-[260px]' : 'w-[340px]'}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      {getUniqueClinicians(patient).map((clinician) => (
                        <div key={`clinician-${patient.patientId}-${clinician.clinicianId}`}>
                          <span
                            data-tooltip-id={`arch-clinician-${patient.patientId}-${clinician.clinicianId}`}
                            className="border inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors duration-200 cursor-help"
                          >
                            {clinician.clinicianName}
                          </span>
                          <Tooltip
                            id={`arch-clinician-${patient.patientId}-${clinician.clinicianId}`}
                            place="top"
                            style={{
                              maxWidth: '250px',
                              whiteSpace: 'normal',
                              backgroundColor: 'rgba(34, 34, 34, 1)',
                              borderRadius: '10px',
                              color: '#fff',
                              fontWeight: "bold"
                            }}
                          >
                            <div>
                              <strong>Assigned Modules:</strong>
                              <ul className="list-disc ml-4 mt-1">
                                {clinician.stages.map(stage => (
                                  <li key={stage.code}>{stage.name}</li>
                                ))}
                              </ul>
                            </div>
                          </Tooltip>
                        </div>
                      ))}
                    </div>
                  </td>

                  <td className="px-3 py-2 text-xs">
                    <div className="flex items-center space-x-2"
                     data-tooltip-id={`archiver-${patient.patientId}`}>
                    <CalendarIcon className="h-4 w-4 text-slate-400" />
                    <p className="text-xs text-slate-600">
                      {formatDateShort(patient.archivedAt)}
                    </p>
                  </div>
                    <Tooltip
                      id={`archiver-${patient.patientId}`}
                      place="top"
                      style={{
                        maxWidth: '250px',
                        whiteSpace: 'normal',
                        backgroundColor: 'rgba(34, 34, 34, 1)',
                        borderRadius: '10px',
                        color: '#fff',
                        fontWeight: "bold"
                      }}
                    >
                      Archived by {patient.archivedByName || 'Unknown'}
                    </Tooltip>
                  </td>

                  <td className="px-3 py-2">
                    <div className="flex items-center space-x-2">
                      <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${reasonInfo.bgColor} ${reasonInfo.color}`}>
                        <IconComponent className="w-3 h-3 mr-1" />
                        {reasonInfo.text}
                      </div>
                      {patient.archiveNote && (
                        <button
                          data-tooltip-id={`archive-note-${patient.patientId}`}
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                          aria-label="View archive note"
                        >
                          <AnnotationIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    {patient.archiveNote && (
                      <Tooltip
                        id={`archive-note-${patient.patientId}`}
                        place="top"
                        style={{
                          maxWidth: '300px',
                          whiteSpace: 'normal',
                          backgroundColor: 'rgba(34, 34, 34, 1)',
                          borderRadius: '10px',
                          color: '#fff',
                          fontWeight: "normal"
                        }}
                      >
                        <div>
                          <strong>Archive Note:</strong>
                          <br />
                          {patient.archiveNote}
                        </div>
                      </Tooltip>
                    )}
                  </td>

                 

                  <td className="px-3 py-2">
                    <div className="flex items-center space-x-2">
                      {overviewPath && (
                          <button
                            onClick={() => {
                              emitCaseloadPatientOpened({
                                patientId: patient.patientId,
                                trigger: 'action_button',
                                caseload: 'archived',
                              });
                              // Record for the recently-viewed shortcut on the dashboard
                              onRecordPatientView?.(patient);
                              navigate(overviewPath, { state: overviewState });
                            }}
                            className={`px-3 py-2 text-xs font-semibold text-white rounded-lg transition-colors duration-200 ${
                              isModular ? 'bg-violet-800 hover:bg-violet-700' : 'bg-gray-600 hover:bg-gray-700'
                            }`}
                          >
                            View Patient
                          </button>
                      )}
                      <button
                        onClick={() => copyPatientIdToClipboard(patient.patientId)}
                        className={`p-2 transition-all duration-200 border rounded-lg ${
                          clickedButton === patient.patientId
                            ? 'text-white bg-blue-600 border-blue-600'
                            : 'text-blue-600 bg-white border-blue-200 hover:bg-blue-50'
                        }`}
                        aria-label="Copy patient ID"
                        title="Copy Patient ID"
                        data-tooltip-id={`arch-action-copy-${patient.patientId}`}
                      >
                        <DocumentDuplicateIcon className="w-4 h-4" />
                      </button>
                      <Tooltip
                        id={`arch-action-copy-${patient.patientId}`}
                        place="top"
                        style={{
                          maxWidth: '250px',
                          whiteSpace: 'normal',
                          backgroundColor: 'rgba(34, 34, 34, 1)',
                          borderRadius: '10px',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: '0.875rem'
                        }}
                        content="Copy ID"
                      />
                      <button
                        onClick={() => openRestoreDialog(patient)}
                        className="p-2 text-green-600 bg-white border border-green-200 rounded-lg hover:bg-green-50 transition-colors duration-200"
                        aria-label="Restore patient to active"
                        title="Restore to Active Patients"
                        data-tooltip-id={`arch-action-restore-${patient.patientId}`}
                      >
                          <ReplyIcon className="w-4 h-4" />
                      </button>
                      <Tooltip
                        id={`arch-action-restore-${patient.patientId}`}
                        place="top"
                        style={{
                          maxWidth: '250px',
                          whiteSpace: 'normal',
                          backgroundColor: 'rgba(34, 34, 34, 1)',
                          borderRadius: '10px',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: '0.875rem'
                        }}
                        content="Restore Patient"
                      />
                      <button
                        onClick={() => setRemovingPatient(patient)}
                        className="p-2 text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors duration-200"
                        aria-label="Remove patient"
                        data-tooltip-id={`arch-action-delete-${patient.patientId}`}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                      <Tooltip
                        id={`arch-action-delete-${patient.patientId}`}
                        place="top"
                        style={{
                          maxWidth: '250px',
                          whiteSpace: 'normal',
                          backgroundColor: 'rgba(34, 34, 34, 1)',
                          borderRadius: '10px',
                          color: '#fff',
                          fontWeight: 'bold',
                          fontSize: '0.875rem'
                        }}
                        content="Remove Patient"
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>{/* end scrollable table region */}

        {/* Pagination — outside the overflow-auto div so it never scrolls horizontally */}
        <CondensedPagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={sortedPatients.length}
          pageSize={patientsPerPage}
          onPageChange={setCurrentPage}
        />
      </div>{/* end outer card */}

      {/* Patient Remove Dialog — rendered unconditionally so Radix Presence
         can animate the exit transition when `open` flips to false. */}
      <PatientRemoveForm
        isOpen={!!removingPatient}
        onClose={() => setRemovingPatient(null)}
        patient={removingPatient}
      />
    </div>
  );
}

export default ArchivedPatientsTable;
